// Billing (PRD §4.10, §8) — live. Real current plan + subscription, usage vs
// quota, upgrade via Amwal checkout, cancel, and the tenant's real invoices.
// The "Payment gateway" card is the operator seam: it mints the callback secret
// and stores the Amwal hosted-checkout URL.
import { useState } from 'react';
import { useI18n } from '../lib/i18n';
import { cn } from '../lib/cn';
import {
  useBilling, usePricing, createCheckout, cancelSubscription, initBillingConfig, validateCoupon, withTax,
  formatMoney, moneyOmr, omrChargeFor, fromOmr, priceIn,
  type CouponCheck, type BillingCycle,
} from '../lib/billing';
import { billingWebhookUrl } from '../lib/firebase';
import { useAuth } from '../lib/useAuth';
import { useAdminAccess } from '../lib/platformAdmins';
import { useConfirm } from '../components/ui/ConfirmDialog';

function fmtDate(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function Bar({ label, used, limit }: { label: string; used: number; limit: number }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const over = used >= limit;
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-ink-2">{label}</span>
        <span className={cn('font-bold', over ? 'text-red-600' : 'text-ink')}>{used.toLocaleString()} / {limit.toLocaleString()}</span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={cn('h-full rounded-full', over ? 'bg-red-500' : pct > 80 ? 'bg-amber-500' : 'bg-primary')} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function Billing() {
  const { t, lang } = useI18n();
  const confirm = useConfirm();
  const b = useBilling();
  // Quote in the visitor's currency; Amwal still settles rials.
  const pricing = usePricing();
  const { user } = useAuth();
  const access = useAdminAccess(user);
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [coupon, setCoupon] = useState('');
  const [check, setCheck] = useState<CouponCheck | null>(null);
  /** Which plan the current quote was computed for. */
  const [checkPlanId, setCheckPlanId] = useState<string | null>(null);

  /**
   * Validate against a specific plan. The quote is per-plan and per-cycle, so
   * checking against "the first paid plan" would show a price for a plan the
   * customer isn't buying once there is more than one.
   */
  async function checkCoupon(planId?: string) {
    const target = planId ?? Object.values(b.catalog).find((p) => !p.isTrial)?.id;
    if (!target) return;
    setBusy(true); setMsg(null);
    try {
      const r = await validateCoupon(coupon.trim(), target, cycle);
      setCheck(r);
      setCheckPlanId(target);
    } catch (e) { setMsg((e as Error).message); } finally { setBusy(false); }
  }

  async function upgrade(planId: string) {
    setBusy(true); setMsg(null);
    try {
      // Only send a code that just validated. The server re-validates anyway —
      // it is the authority — so a stale or wrong-plan code fails loudly there.
      // Only send a code that validated for THIS plan; the server re-validates
      // regardless, so a mismatch fails loudly rather than surprising the buyer.
      const useCode = check?.ok && checkPlanId === planId ? check.code : undefined;
      const res = await createCheckout(planId, cycle, useCode);
      if (res.checkoutUrl) { window.location.href = res.checkoutUrl; return; }
      // A 100%-off code settles server-side with no gateway step.
      if (res.activated) { setCoupon(''); setCheck(null); setMsg(t('bill.msg.activated')); return; }
      setMsg(t('bill.msg.noGateway'));
    } catch (e) {
      // Turn `coupon_wrong_plan` & friends into the same wording as the preview.
      const raw = (e as Error).message;
      const m = /coupon_(\w+)/.exec(raw);
      setMsg(m ? t(`bill.coupon.err.${m[1]}`) : raw);
    } finally { setBusy(false); }
  }

  async function cancel() {
    if (!(await confirm({
      title: t('bill.cancel.title'),
      body: t('bill.cancel.confirm'),
      confirmLabel: t('bill.cancel.title'),
      cancelLabel: t('common.keep'),
      danger: true,
    }))) return;
    setBusy(true); setMsg(null);
    try { await cancelSubscription(); setMsg(t('bill.cancel.done')); }
    catch (e) { setMsg((e as Error).message); } finally { setBusy(false); }
  }

  const q = b.plan.quotas;

  return (
    <div>
      <h1 className="text-[22px] font-extrabold text-ink">{t('bill.title')}</h1>
      <p className="mb-5 text-ink-3">{t('bill.subtitle')}</p>

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          {/* Current plan + usage */}
          <div className="rounded-card border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-extrabold text-ink">{t('bill.plan.title')}</h2>
              <span className="rounded-full bg-primary-50 px-2.5 py-0.5 text-[11px] font-bold text-primary">{b.plan.name}</span>
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-bold text-ink-2">{b.status}</span>
              {b.cancelAtPeriodEnd && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-bold text-amber-700">{t('bill.plan.canceling')}</span>}
            </div>
            {/* A renewal date on the free-forever plan is both meaningless and
                alarming — nothing is ever charged and nothing lapses. Legacy
                tenants still carry an old periodEnd, so key off the plan. */}
            {b.plan?.isFree ? (
              <p className="mt-2 text-xs font-semibold text-success">{t('bill.free.forever')}</p>
            ) : (
              <p className="mt-2 text-xs text-ink-3">{t('bill.renewal.title')}: <b className="text-ink">{fmtDate(b.renewalAt)}</b></p>
            )}

            <div className="mt-4 space-y-3 border-t border-slate-200 pt-4">
              <Bar label={t('bill.usage.messages')} used={b.usageMessages} limit={q.messagesPerMonth} />
              <Bar label={t('bill.usage.subscribers')} used={b.subscribers} limit={q.subscribers} />
            </div>
          </div>

          {/* Plans */}
          <div className="rounded-card border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-extrabold text-ink">{t('bill.plans.title')}</h2>
              {/* Detected from the visitor's country; overridable because
                  someone travelling should still see their real prices. */}
              <label className="flex items-center gap-1.5 text-[11px] text-ink-3">
                <span className="sr-only">{t('bill.currency.label')}</span>
                <select
                  value={pricing.currency}
                  onChange={(e) => pricing.setCurrency(e.target.value)}
                  aria-label={t('bill.currency.label')}
                  className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-bold text-ink-2"
                >
                  {Object.keys(pricing.currencies).sort().map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
              {/* A coupon preview is priced per cycle — drop it on toggle so it can't lie. */}
              <div className="inline-flex rounded-full bg-slate-100 p-1">
                {(['monthly', 'yearly'] as BillingCycle[]).map((c) => (
                  <button key={c} type="button" onClick={() => { setCycle(c); setCheck(null); }}
                    className={cn('rounded-full px-4 py-1.5 text-xs font-bold', cycle === c ? 'bg-white text-primary shadow-sm' : 'text-ink-2')}>
                    {t(`bill.cycle.${c}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Three tiers: keep them on one row from `sm` up so Free, Pro and
                Business read as a single comparison rather than 2 + 1. */}
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Object.values(b.catalog).map((p) => {
                // The local price is what the customer is quoted; the rial we
                // actually charge is derived from it. When a plan has no row
                // for this currency (e.g. plans/* not seeded yet) we convert
                // its rial figure instead — the card must always answer to the
                // currency picker, never silently drop back to rials.
                const omrBase = cycle === 'yearly' ? p.yearlyUsd : p.monthlyUsd;
                const local = priceIn(p.prices, pricing.currency, cycle);
                const hasLocal = local !== null;
                const price = p.isFree
                  ? 0
                  : hasLocal
                    ? (local as number)
                    : fromOmr(omrBase, pricing.currency, pricing.currencies);
                const omr = hasLocal
                  ? omrChargeFor(local as number, pricing.currency, pricing.currencies)
                  : omrBase;
                const shownCurrency = pricing.currency;
                const isCurrent = p.id === b.planId;
                return (
                  <div key={p.id} className={cn('rounded-[12px] border p-4', isCurrent ? 'border-primary bg-primary-50/40' : 'border-slate-200')}>
                    <div className="flex items-center justify-between gap-2">
                      <b className="block text-sm text-ink">{p.name}</b>
                      {/* The free month is granted once per workspace, so hide
                          the promise from anyone who has already taken it. */}
                      {!p.isFree && (p.trialDays ?? 0) > 0 && !b.freeMonthUsed && (
                        <span className="rounded-full bg-success-50 px-2 py-0.5 text-[10px] font-extrabold text-success">
                          {t('bill.freeMonth.badge')}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-end gap-1">
                      <span className="text-2xl font-extrabold text-ink">
                        {formatMoney(price, shownCurrency, pricing.currencies, lang)}
                      </span>
                      <span className="mb-1 text-xs text-ink-3">
                        /{t(`bill.per.${cycle === 'monthly' ? 'month' : 'year'}`)}
                      </span>
                    </div>
                    {p.isFree
                      ? <p className="mt-1 text-[11px] font-semibold text-success">{t('bill.free.forever')}</p>
                      : price > 0 && shownCurrency !== 'OMR' && (
                        // Quoted locally, settled in rial by Amwal — say so
                        // before they reach the gateway, not after.
                        <p className="mt-1 text-[11px] text-ink-3">{t('bill.billedIn')} {moneyOmr(omr)}</p>
                      )}
                    {/* Disclose VAT before the customer is sent to the gateway —
                        being charged more than the number on screen is not ok. */}
                    {b.taxRatePct > 0 && price > 0 && (
                      <p className="mt-1 text-[11px] text-ink-3">
                        + {b.taxRatePct}% {b.taxLabel} · {t('bill.tax.total')} <b className="text-ink">{formatMoney(withTax(price, b.taxRatePct), shownCurrency, pricing.currencies, lang)}</b>
                      </p>
                    )}
                    <ul className="mt-3 space-y-1 text-xs text-ink-2">
                      <li>{p.quotas.messagesPerMonth.toLocaleString()} {t('bill.usage.messages')}</li>
                      <li>{p.quotas.subscribers.toLocaleString()} {t('bill.usage.subscribers')}</li>
                      <li>{p.quotas.teamSeats} {t('perm.title')}</li>
                    </ul>
                    <div className="mt-4">
                      {isCurrent ? (
                        <button type="button" disabled className="w-full rounded-[9px] border border-slate-200 px-4 py-2 text-[13px] font-bold text-ink-3">{t('bill.plan.current')}</button>
                      ) : p.isTrial ? (
                        <button type="button" onClick={() => void cancel()} disabled={busy} className="w-full rounded-[9px] border border-slate-200 px-4 py-2 text-[13px] font-bold text-ink disabled:opacity-40">{t('bill.plan.downgrade')}</button>
                      ) : (
                        <button type="button" onClick={() => void upgrade(p.id)} disabled={busy} className="w-full rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40">{busy ? '…' : t('bill.plan.upgrade')}</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Discount code — validated before checkout so the price shown is real. */}
            <div className="mt-4 border-t border-slate-200 pt-4">
              <span className="mb-1 block text-xs font-bold text-ink-2">{t('bill.coupon.label')}</span>
              <div className="flex flex-wrap gap-2">
                <input
                  value={coupon}
                  onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setCheck(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') void checkCoupon(); }}
                  placeholder={t('bill.coupon.placeholder')}
                  className="w-full max-w-[220px] rounded-[9px] border border-slate-200 px-3 py-2 font-mono text-sm uppercase outline-none focus:border-primary"
                />
                <button type="button" onClick={() => void checkCoupon()} disabled={busy || coupon.trim().length < 3}
                  className="rounded-[9px] border border-slate-200 px-4 py-2 text-[13px] font-bold text-ink-2 hover:border-primary hover:text-primary disabled:opacity-40">
                  {t('bill.coupon.apply')}
                </button>
                {check?.ok && (
                  <button type="button" onClick={() => { setCoupon(''); setCheck(null); }}
                    className="rounded-[9px] px-2 py-2 text-xs font-bold text-ink-3 hover:text-ink">✕</button>
                )}
              </div>
              {check?.ok && (
                <div className="mt-2 text-xs font-bold text-primary">
                  ✓ {check.label}
                  <div className="mt-1 font-normal text-ink-2">
                    {/* Coupon maths happens server-side in OMR (that is what
                        gets charged), so show these as rials rather than
                        pretending they are the local quote. */}
                    <div>{t('bill.tax.subtotal')}: <b className="text-ink">{moneyOmr(check.subtotalUsd)}</b>
                      <s className="ms-2 text-ink-3">{moneyOmr(check.listAmountUsd)}</s></div>
                    {check.taxUsd > 0 && (
                      <div>{check.taxLabel ?? 'VAT'} ({check.taxRatePct}%): <b className="text-ink">{moneyOmr(check.taxUsd)}</b></div>
                    )}
                    <div>{t('bill.coupon.youPay')} <b className="text-primary">{moneyOmr(check.amountUsd)}</b></div>
                  </div>
                </div>
              )}
              {check && !check.ok && (
                <p className="mt-2 text-xs font-bold text-red-600">{t(`bill.coupon.err.${check.reason}`)}</p>
              )}
            </div>

            {msg && <div className="mt-3 rounded-[9px] bg-surface px-3 py-2 text-xs font-semibold text-ink-2">{msg}</div>}
          </div>

          {/* Invoices */}
          <div className="rounded-card border border-slate-200 bg-white shadow-sm">
            <div className="p-4 text-sm font-extrabold text-ink">{t('bill.invoices.title')}</div>
            {b.invoices.length === 0 ? (
              <p className="px-4 pb-5 text-sm text-ink-3">{t('bill.invoices.empty')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[460px] border-collapse">
                  <thead>
                    <tr>
                      {['bill.col.invoice', 'bill.col.date', 'bill.col.amount', 'bill.col.status'].map((k) => (
                        <th key={k} className="whitespace-nowrap bg-primary-50 px-3.5 py-3 text-start text-[11px] font-bold uppercase tracking-wide text-ink-3">{t(k)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.invoices.map((inv) => (
                      <tr key={inv.id} className="border-t border-slate-200">
                        <td className="whitespace-nowrap px-3.5 py-3 font-mono text-xs text-ink-2">{inv.id.slice(0, 8)}</td>
                        <td className="whitespace-nowrap px-3.5 py-3 text-[13px] text-ink-2">{fmtDate(inv.createdAt)}</td>
                        {/* Invoices show what was actually taken from the card,
                            which Amwal settles in rial — not the USD headline. */}
                        <td className="whitespace-nowrap px-3.5 py-3 text-[13px] font-bold text-ink">{moneyOmr(inv.amountUsd)}</td>
                        <td className="px-3.5 py-3 text-[13px]">
                          <span className="inline-flex items-center gap-1.5 font-semibold">
                            <span className={cn('h-2 w-2 rounded-full', inv.status === 'paid' ? 'bg-accent' : inv.status === 'failed' ? 'bg-red-500' : 'bg-amber-500')} aria-hidden />
                            {t(`bill.invoice.${inv.status}`)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar: renewal + payment gateway */}
        <div className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          <div className="rounded-card border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-extrabold text-ink">{t('bill.renewal.title')}</h2>
            {b.plan?.isFree ? (
              <>
                <p className="mt-2 text-2xl font-extrabold text-success">{t('bill.free.never')}</p>
                <p className="mt-1 text-xs text-ink-3">{t('bill.free.forever')}</p>
              </>
            ) : (
              <>
                <p className="mt-2 text-2xl font-extrabold text-primary">{fmtDate(b.renewalAt)}</p>
                <p className="mt-1 text-xs text-ink-3">{t('bill.renewal.note')}</p>
              </>
            )}
            {(b.bonusMessages > 0 || (b.freeUntil && b.freeUntil > Date.now())) && (
              <p className="mt-3 rounded-[9px] bg-primary-50 px-3 py-2 text-xs font-semibold text-primary">
                {b.freeUntil && b.freeUntil > Date.now()
                  ? `${t('bill.credit.freeUntil')} ${fmtDate(b.freeUntil)}`
                  : `+${b.bonusMessages.toLocaleString()} ${t('bill.credit.bonus')}`}
              </p>
            )}
          </div>
          {/* Operator-only seam (billingInitConfig requires platformAdmin). A
              tenant owner must never see the platform webhook URL or get a raw
              permission-denied from a button on their own billing page. */}
          {access.allowed && <PaymentGatewayCard />}
        </div>
      </div>
    </div>
  );
}

function PaymentGatewayCard() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function init() {
    setBusy(true); setErr(null);
    try {
      const res = await initBillingConfig(url.trim() || undefined);
      setSecret(res.callbackSecret);
      if (res.amwalCheckoutUrl) setUrl(res.amwalCheckoutUrl);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <div className="rounded-card border border-slate-200 bg-white p-5 shadow-sm">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between">
        <h2 className="text-sm font-extrabold text-ink">{t('bill.gateway.title')}</h2>
        <span className="text-ink-3">{open ? '▾' : '▸'}</span>
      </button>
      <p className="mt-1 text-[11px] text-ink-3">{t('bill.payment.amwal')}</p>

      {open && (
        <div className="mt-3 space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs font-bold text-ink-2">{t('bill.gateway.url')}</span>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://checkout.amwalpg.com/..." aria-label={t('bill.gateway.url')} className="w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary" />
          </label>
          <div>
            <span className="mb-1 block text-xs font-bold text-ink-2">{t('bill.gateway.webhook')}</span>
            <code className="block overflow-x-auto rounded-[8px] bg-surface px-2 py-1.5 font-mono text-[11px] text-ink-2">{billingWebhookUrl}</code>
          </div>
          {secret && (
            <div className="rounded-[9px] border border-primary bg-primary-50 p-2.5">
              <span className="block text-[11px] font-bold text-primary">{t('bill.gateway.secret')}</span>
              <code className="mt-1 block overflow-x-auto rounded bg-white px-2 py-1 font-mono text-[11px] text-ink">{secret}</code>
            </div>
          )}
          {err && <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}
          <button type="button" onClick={() => void init()} disabled={busy} className="w-full rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40">
            {busy ? '…' : t('bill.gateway.save')}
          </button>
        </div>
      )}
    </div>
  );
}
